import jsPDF from "jspdf";
import moment from "moment";

const MARGEN_X = 10;
const MARGEN_SUPERIOR = 10;
const MARGEN_INFERIOR = 12;
const ALTO_PAGINA = 210; // A4 horizontal
const ANCHO_UTIL = 277; // 297 - 2 * MARGEN_X
const ALTO_ENCABEZADO_TABLA = 7;
const ALTO_LINEA = 3.2;
const PADDING_CELDA = 1.2;
const ALTO_MINIMO_FILA = 6;
const FUENTE_CELDA = 7;
const TAMAÑO_FUENTE_TABLA = 7;
const TAMAÑO_FUENTE_TITULO_TABLA = 10;

/**
 * Formatea un número con locale es-CL.
 * @param {number|string} valor - Valor numérico.
 * @param {number} min - Mínimo de decimales.
 * @param {number} max - Máximo de decimales.
 * @return {string} Número formateado.
 */
const formatNumero = (valor, min = 2, max = 2) =>
  parseFloat(valor).toLocaleString("es-CL", {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  });

/**
 * Formatea una tarifa: respeta "Tarifa Variable" y agrega sufijo UF.
 * @param {number|string} valor - Valor de la tarifa.
 * @return {string} Tarifa formateada.
 */
const fmtTarifa = (valor) =>
  valor === "Tarifa Variable"
    ? valor
    : valor != null && valor !== "" && !isNaN(parseFloat(valor))
    ? `${formatNumero(valor)} UF`
    : "";

/**
 * Formatea una tarifa: filas CLP muestran el valor UF y debajo el
 * equivalente en pesos (igual que la tabla web); resto muestra UF.
 * @param {number|string} valor - Valor de la tarifa (en UF).
 * @param {string} moneda - Moneda de la fila.
 * @param {number} valorUf - Valor de la UF en CLP.
 * @return {string} Tarifa formateada (con \n si es CLP).
 */
const fmtTarifaMoneda = (valor, moneda, valorUf) =>
  valor === "Tarifa Variable"
    ? valor
    : valor != null && valor !== "" && !isNaN(parseFloat(valor))
    ? moneda === "CLP"
      ? `${formatNumero(valor)} UF\n$${formatNumero(
          Math.round(parseFloat(valor) * valorUf),
          0,
          0
        )} CLP`
      : `${formatNumero(valor)} UF`
    : "";

/**
 * Formatea un valor libre (tarifa cliente) sin sufijo.
 * @param {number|string} valor - Valor numérico.
 * @return {string} Número formateado o vacío.
 */
const fmtNumeroLibre = (valor) =>
  valor != null && valor !== "" && !isNaN(parseFloat(valor))
    ? formatNumero(valor, 0, 5)
    : "";

/**
 * Formatea una fecha "YYYY-MM-DD" a "DD/MM/YYYY".
 * @param {string} valor - Fecha de entrada.
 * @return {string} Fecha formateada o el valor original.
 */
const fmtFecha = (valor) => {
  if (!valor) return "";
  const m = moment(valor, "YYYY-MM-DD", true);
  return m.isValid() ? m.format("DD/MM/YYYY") : String(valor);
};

/**
 * Normaliza el tramo: "null" o vacío se muestra como cadena vacía.
 * @param {string} tramo - Tramo del ítem.
 * @return {string} Tramo normalizado.
 */
const fmtTramo = (tramo) => (tramo && tramo !== "null" ? tramo : "");

/**
 * Normaliza la unidad de medida (m**2 -> m²).
 * @param {string} unidad - Unidad de entrada.
 * @return {string} Unidad formateada.
 */
const fmtUnidad = (unidad) => (unidad === "m**2" ? "m²" : unidad ?? "");

/**
 * Normaliza el descuento porcentual.
 * @param {number} descuento - Descuento de entrada.
 * @return {string} Descuento formateado con %.
 */
const fmtDescuento = (descuento) =>
  `${formatNumero(isFinite(descuento) ? descuento : 0, 0, 4)} %`;

/**
 * Crea la definición de una columna de tabla.
 * @param {string} header - Título de la columna.
 * @param {number} width - Ancho en mm.
 * @param {Function} value - Extractor del valor de la fila.
 * @param {string} align - Alineación: left | center | right.
 * @return {Object} Definición de columna.
 */
const col = (header, width, value, align = "left") => ({
  header,
  width,
  value,
  align,
});

const columnasArriendo = (valorUf, filas = []) => [
  col("Nombre", 55, (r) => r.descripcion ?? ""),
  col("Tramo", 16, (r) => fmtTramo(r.tramo), "center"),
  col("Desde", 20, (r) => fmtFecha(r.desde), "center"),
  col("Hasta", 20, (r) => fmtFecha(r.hasta), "center"),
  col("Doc. Cobro", 16, (r) => r.cobro ?? ""),
  col("Tipo", 16, (r) => (r.tipo === "Incluido" ? "Incluido" : "Adicional"), "center"),
  col("Medida", 12, (r) => fmtUnidad(r.unidad), "center"),
  col("Cantidad", 12, (r) => r.cantidad ?? "", "right"),
  col("Tarifa Base", 16, (r) => fmtTarifa(r.tarifa_base), "right"),
  col("Moneda", 14, (r) => r.moneda ?? "", "center"),
  col("Tarifa Cliente", 20, (r) => fmtNumeroLibre(r.tarifa_cliente), "right"),
  ...(filas.some((r) => r.moneda !== "CLP")
    ? [col("Desc. %", 12, (r) => (r.moneda === "CLP" ? "" : fmtDescuento(r.descuento)), "right")]
    : []),
  col("Tarifa Diaria", 20, (r) => fmtTarifaMoneda(r.tarifa_diaria, r.moneda, valorUf), "right"),
  col("Tarifa Mensual", 24, (r) => fmtTarifaMoneda(r.tarifa_mensual, r.moneda, valorUf), "right"),
];

const columnasServicios = (valorUf, filas = []) => [
  col("Nombre", 83, (r) => r.descripcion ?? ""),
  col("Tramo", 16, (r) => fmtTramo(r.tramo), "center"),
  col("Desde", 20, (r) => fmtFecha(r.desde), "center"),
  col("Hasta", 20, (r) => fmtFecha(r.hasta), "center"),
  col("Doc. Cobro", 16, (r) => r.cobro ?? ""),
  col("Medida", 12, (r) => fmtUnidad(r.unidad), "center"),
  col("Tarifa Base", 16, (r) => fmtTarifa(r.tarifa_base), "right"),
  col("Moneda", 14, (r) => r.moneda ?? "", "center"),
  col("Tarifa Cliente", 20, (r) => fmtNumeroLibre(r.tarifa_cliente), "right"),
  ...(filas.some((r) => r.moneda !== "CLP")
    ? [col("Desc. %", 12, (r) => (r.moneda === "CLP" ? "" : fmtDescuento(r.descuento)), "right")]
    : []),
  col("Tarifa Diaria", 20, (r) => fmtTarifaMoneda(r.tarifa_diaria, r.moneda, valorUf), "right"),
  col("Tarifa Mensual", 24, (r) => fmtTarifaMoneda(r.tarifa_mensual, r.moneda, valorUf), "right"),
];

const columnasUnicos = [
  col("Nombre", 90, (r) => r.descripcion ?? ""),
  col("Cuota", 30, (r) => fmtTramo(r.tramo), "center"),
  col("Doc. Cobro", 30, (r) => r.cobro ?? ""),
  col("Moneda", 25, (r) => r.moneda ?? "", "center"),
  col("Tarifa Cliente", 30, (r) => fmtNumeroLibre(r.tarifa_cliente), "right"),
];

/**
 * Dibuja la cabecera del documento (logo + datos de la cotización).
 * @param {jsPDF} pdf - Documento PDF.
 * @param {Object} datos - Datos de cabecera (idCotizacion, cotizacion, cliente, productos).
 * @param {string|null} logoDataUrl - Logo en dataURL (opcional).
 * @return {number} Posición Y final.
 */
const dibujarCabecera = (pdf, datos, logoDataUrl) => {
  const { idCotizacion, cotizacion, cliente, productos } = datos;

  if (logoDataUrl) {
    pdf.addImage(logoDataUrl, "PNG", MARGEN_X, MARGEN_SUPERIOR, 40, 20);
  }

  const xTexto = logoDataUrl ? 55 : MARGEN_X;
  const anchoTexto = ANCHO_UTIL - (xTexto - MARGEN_X);

  const esAsignacion = Boolean(cotizacion?.folio);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.text(
    esAsignacion
      ? `Asignación Folio: ${cotizacion.folio}`
      : `Cotización: ${idCotizacion}`,
    xTexto,
    logoDataUrl ? 17 : 14
  );

  pdf.setFontSize(9);
  let y = logoDataUrl ? 24 : 21;

  const escribirLinea = (etiqueta, valor) => {
    pdf.setFont("helvetica", "bold");
    pdf.text(etiqueta, xTexto, y);
    const anchoEtiqueta = pdf.getTextWidth(etiqueta);
    pdf.setFont("helvetica", "normal");
    const lineas = pdf.splitTextToSize(
      String(valor ?? ""),
      anchoTexto - anchoEtiqueta - 2
    );
    lineas.forEach((linea, i) =>
      pdf.text(linea, xTexto + anchoEtiqueta + 1.5, y + i * 4)
    );
    y += Math.max(5, lineas.length * 4);
  };

  escribirLinea("Cliente: ", cliente?.razon_social?.toUpperCase() ?? "");
  if (esAsignacion) {
    escribirLinea("Cotización N°: ", idCotizacion);
  }
  escribirLinea("Productos: ", (productos ?? []).join(" | "));
  escribirLinea("Empresa Arrendadora: ", cotizacion?.empresa ?? "");

  const estado = cotizacion?.estado?.estado;
  if (estado === "ASIGNACIÓN") {
    escribirLinea("Estado: ", estado);
  }

  return y + 4;
};

/**
 * Dibuja una tabla con grilla, encabezado sombreado y salto de página.
 * @param {jsPDF} pdf - Documento PDF.
 * @param {Object} config - Configuración de la tabla.
 * @param {string} config.titulo - Título de la sección.
 * @param {Array} config.columnas - Definición de columnas.
 * @param {Array} config.filas - Filas de datos.
 * @param {number} config.y - Posición Y inicial.
 * @return {number} Posición Y final.
 */
const dibujarTabla = (pdf, { titulo, columnas, filas, y }) => {
  if (!filas || filas.length === 0) return y;

  // Ajusta los anchos de las columnas para que encabezado y grilla
  // llenen exactamente el ancho útil de la página
  const anchoColumnas = columnas.reduce((suma, c) => suma + c.width, 0);
  const factor = ANCHO_UTIL / anchoColumnas;
  const columnasAjustadas = columnas.map((c) => ({
    ...c,
    width: c.width * factor,
  }));
  columnas = columnasAjustadas;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(TAMAÑO_FUENTE_TITULO_TABLA);
  pdf.text(titulo, MARGEN_X, y + 5);

  const dibujarEncabezado = (yEncabezado) => {
    pdf.setFillColor(230);
    pdf.rect(MARGEN_X, yEncabezado, ANCHO_UTIL, ALTO_ENCABEZADO_TABLA, "FD");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(FUENTE_CELDA);
    let x = MARGEN_X;
    columnas.forEach((columna) => {
      pdf.text(
        columna.header,
        x + columna.width / 2,
        yEncabezado + 4.7,
        { align: "center" }
      );
      x += columna.width;
    });
    return yEncabezado + ALTO_ENCABEZADO_TABLA;
  };

  let cursorY = dibujarEncabezado(y + 8);

  filas.forEach((fila) => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(TAMAÑO_FUENTE_TABLA);

    const celdas = columnas.map((columna) => {
      const texto = String(columna.value(fila) ?? "");
      const lineas = pdf.splitTextToSize(
        texto,
        columna.width - PADDING_CELDA * 2
      );
      return { lineas, align: columna.align, width: columna.width };
    });

    const maxLineas = Math.max(
      ...celdas.map((celda) => celda.lineas.length),
      1
    );
    const altoFila = Math.max(
      ALTO_MINIMO_FILA,
      maxLineas * ALTO_LINEA + PADDING_CELDA * 2
    );

    if (cursorY + altoFila > ALTO_PAGINA - MARGEN_INFERIOR) {
      pdf.addPage();
      cursorY = MARGEN_SUPERIOR;
      cursorY = dibujarEncabezado(cursorY);
    }

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(TAMAÑO_FUENTE_TABLA);

    let x = MARGEN_X;
    celdas.forEach((celda) => {
      pdf.rect(x, cursorY, celda.width, altoFila);
      const lineas = celda.lineas.length ? celda.lineas : [""];
      lineas.forEach((linea, i) => {
        const anchoTexto = pdf.getTextWidth(linea);
        let textoX = x + PADDING_CELDA;
        if (celda.align === "right") {
          textoX = x + celda.width - PADDING_CELDA - anchoTexto;
        } else if (celda.align === "center") {
          textoX = x + (celda.width - anchoTexto) / 2;
        }
        pdf.text(linea, textoX, cursorY + PADDING_CELDA + ALTO_LINEA * (i + 1) - 0.6);
      });
      x += celda.width;
    });

    cursorY += altoFila;
  });

  return cursorY + 4;
};

/**
 * Dibuja una línea de subtotal (etiqueta en negrita + valor).
 * @param {jsPDF} pdf - Documento PDF.
 * @param {string} etiqueta - Etiqueta del subtotal.
 * @param {number} valor - Valor numérico.
 * @param {number} y - Posición Y inicial.
 * @return {number} Posición Y final.
 */
const dibujarSubtotal = (pdf, etiqueta, valor, y) => {
  if (y + 6 > ALTO_PAGINA - MARGEN_INFERIOR) {
    pdf.addPage();
    y = MARGEN_SUPERIOR;
  }
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.text(etiqueta, MARGEN_X, y + 4);
  const anchoEtiqueta = pdf.getTextWidth(etiqueta);
  pdf.setFont("helvetica", "normal");
  pdf.text(`${formatNumero(valor)} UF`, MARGEN_X + anchoEtiqueta + 1.5, y + 4);
  return y + 7;
};

/**
 * Dibuja la línea de total (etiqueta y valor en negrita).
 * @param {jsPDF} pdf - Documento PDF.
 * @param {string} etiqueta - Etiqueta del total.
 * @param {number} valor - Valor numérico.
 * @param {number} y - Posición Y inicial.
 * @return {number} Posición Y final.
 */
const dibujarTotal = (pdf, etiqueta, valor, y) => {
  if (y + 6 > ALTO_PAGINA - MARGEN_INFERIOR) {
    pdf.addPage();
    y = MARGEN_SUPERIOR;
  }
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.text(etiqueta, MARGEN_X, y + 4);
  pdf.text(`${formatNumero(valor)} UF`, MARGEN_X + pdf.getTextWidth(etiqueta) + 1.5, y + 4);
  return y + 8;
};

/**
 * Genera el PDF de la cotización a partir de los datos ya cargados en
 * RevisarCotizacion (mismos datos que alimentan las tablas en pantalla),
 * dibujando cabecera y tablas nativas con jsPDF, y lo abre en una pestaña nueva.
 * @param {Object} datos - Datos de la cotización.
 * @param {number|string} datos.idCotizacion - ID de la cotización.
   * @param {Object} datos.cotizacion - Cabecera de la cotización (empresa, estado, folio).
 * @param {Object} datos.cliente - Cliente (razon_social).
 * @param {Array} datos.productos - Nombres de productos de la cotización.
 * @param {Array} datos.arriendos - Filas de arriendo (flattenedDataArriendo).
 * @param {Array} datos.servicios - Filas de servicios.
 * @param {Array} datos.serviciosUnicos - Filas de servicios únicos.
 * @param {number} datos.subtotalArriendo - Arriendo promedio mensual.
 * @param {number} datos.subtotalServicios - Servicios promedio mensual.
 * @return {Promise<void>} Promesa que se resuelve al abrir el PDF generado.
 */
const exportarCotizacionAPdf = async ({
  idCotizacion,
  cotizacion,
  cliente,
  productos = [],
  arriendos = [],
  servicios = [],
  serviciosUnicos = [],
  subtotalArriendo = 0,
  subtotalServicios = 0,
  monedaOptions = [],
}) => {
  const valorUf = monedaOptions?.find((m) => m.codigo === "UF")?.tipoCambio || 1;
  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });
  pdf.setProperties({ title: `Cotizacion ${idCotizacion}` });

  // Cargar logo desde public/logoCBQ.png (ruta segura con PUBLIC_URL)
  const logoUrl = `${process.env.PUBLIC_URL}/logoCBQ.png`;
  let logoDataUrl = null;
  try {
    const resp = await fetch(logoUrl);
    const blob = await resp.blob();
    logoDataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn(`No se pudo cargar el logo desde ${logoUrl}`, e);
  }

  let y = dibujarCabecera(pdf, { idCotizacion, cotizacion, cliente, productos }, logoDataUrl);

  if (arriendos.length > 0) {
    y = dibujarTabla(pdf, {
      titulo: `TARIFA MENSUAL (OPEX) - ARRIENDO - Valor UF: $${formatNumero(valorUf)} al ${moment().startOf("month").format("DD/MM/YYYY")}`,
      columnas: columnasArriendo(valorUf, arriendos),
      filas: arriendos,
      y,
    });
    y = dibujarSubtotal(pdf, "Arriendo Promedio Mensual:", subtotalArriendo, y);
  }

  if (servicios.length > 0) {
    y = dibujarTabla(pdf, {
      titulo: "TARIFA MENSUAL (OPEX) - SERVICIOS",
      columnas: columnasServicios(valorUf, servicios),
      filas: servicios,
      y,
    });
    y = dibujarSubtotal(pdf, "Servicios Promedio Mensual:", subtotalServicios, y);
  }

  if (arriendos.length > 0 || servicios.length > 0) {
    y = dibujarTotal(
      pdf,
      "Total:",
      parseFloat(subtotalArriendo) + parseFloat(subtotalServicios),
      y
    );
  }

  if (serviciosUnicos.length > 0) {
    dibujarTabla(pdf, {
      titulo: "TARIFA UNICA (CAPEX) - SERVICIOS ÚNICOS",
      columnas: columnasUnicos,
      filas: serviciosUnicos,
      y,
    });
  }

  const pdfData = pdf.output("bloburl");
  window.open(pdfData);
};

export default exportarCotizacionAPdf;
